'use client';

/**
 * AvatarRatingBadge — five reviewers' faces, the stars and "from N reviews"
 * (Senja sunset, 2026-09-26). Replaces Senja's "Rating with photos" widget
 * 1f2fc9d3 on /pro-member (and /pro-member-v12).
 *
 * Lars: "when you hover them, they will grow a little bit … When I click it,
 * it opens … the whole review in the pop-up modal." Each face is a button
 * that opens THAT person's full review in ReviewModal. N is counted live from
 * our testimonials database (lib/review-feed.ts reviewTotals); when the count
 * cannot be read, the line is left out rather than showing a stale number.
 * Values: Aria §2 (reviews.css). `ring` = the section's own background, so the
 * ring reads as a cut-out between neighbouring faces.
 */
import { useState } from 'react';
import type { ReviewItem } from '@/lib/review-feed';
import ReviewModal, { ReviewAvatar, ReviewStars } from './ReviewModal';
import './reviews.css';

export default function AvatarRatingBadge({
  items,
  count,
  average,
  ring,
}: {
  items: ReviewItem[];
  count: number | null;
  average: number | null;
  ring?: string;
}) {
  const [open, setOpen] = useState<ReviewItem | null>(null);
  if (items.length === 0) return null;
  const stars = average !== null ? Math.round(average) : 5;
  return (
    <div
      className="rv-badge"
      data-testid="avatar-rating-badge"
      style={ring ? ({ '--rv-badge-ring': ring } as React.CSSProperties) : undefined}
    >
      <ul className="rv-badge__avatars">
        {items.map((it, i) => (
          <li key={it.id} className="rv-badge__item" style={{ '--rv-z': i + 1 } as React.CSSProperties}>
            <button
              type="button"
              className="rv-badge__btn"
              aria-label={`Read ${it.name}'s review`}
              data-review-id={it.id}
              onClick={() => setOpen(it)}
            >
              <ReviewAvatar item={it} className="rv-badge__img" />
            </button>
          </li>
        ))}
      </ul>
      <div className="rv-badge__meta">
        <ReviewStars rating={stars} />
        {count !== null ? <p className="rv-badge__count">from {count} reviews</p> : null}
      </div>
      <ReviewModal item={open} onClose={() => setOpen(null)} />
    </div>
  );
}
